import { useEffect } from 'react'
import type { ECharts } from 'echarts'
import type { Time } from 'lightweight-charts'
import { SCREENER_INTERVAL_SECONDS } from '@/lib/screenerHistory'

type PointerEvent = {
  escapeConnect?: boolean
  axesInfo?: Array<{ axisDim: string; value?: number }>
}

const groups = new Map<string, Set<ECharts>>()

/** Read the live pointer without getOption(), which clones the entire history. */
export function chartTooltipTime(chart: ECharts | undefined, fallback: number): number {
  // ECharts' tooltip axisValue is still snapped to the nearest series point,
  // even when its visible time cursor is unsnapped. The live pointer model is
  // updated before formatting; using it avoids showing a future sparse sample.
  const model = (chart as unknown as { getModel?: () => {
    getComponent: (name: string) => { coordSysAxesInfo?: { axesInfo?: Record<string, {
      axis: { dim: string }
      axisPointerModel: { get: (name: string) => unknown }
    }> } } | undefined
  } } | undefined)?.getModel?.()
  const axes = model?.getComponent('axisPointer')?.coordSysAxesInfo?.axesInfo
  const pointer = Object.values(axes ?? {}).find(info => info.axis.dim === 'x')?.axisPointerModel
  const value = pointer?.get('value')
  return pointer?.get('status') === 'show' && typeof value === 'number' && Number.isFinite(value)
    ? value : fallback
}

/** Link by time: ECharts' default relay also requires a matching series data index. */
export function bindChartCrosshair(chart: ECharts, group: string, onTime: (time: Time | null) => void, interval: string) {
  chart.group = group
  const peers = groups.get(group) ?? new Set<ECharts>()
  groups.set(group, peers)
  peers.add(chart)

  const pointer = (raw: unknown) => {
    const event = raw as PointerEvent
    // Keep native linked zoom, but prevent its index-based pointer relay from
    // hiding sparse charts or choosing a different observation in each panel.
    event.escapeConnect = true
    const value = event.axesInfo?.find((axis) => axis.axisDim === 'x')?.value
    // Snap to the shared interval grid, not each exchange's nearest sample:
    // sparse exchanges must still show the same time, including inside gaps.
    const step = (SCREENER_INTERVAL_SECONDS[interval] || 60) * 1000
    const time = typeof value === 'number' && Number.isFinite(value) ? Math.round(value / step) * step : null
    if (time != null && time !== value) {
      const level = event.axesInfo?.find((axis) => axis.axisDim === 'y')?.value
      const y = typeof level === 'number' && Number.isFinite(level)
        ? Number(chart.convertToPixel({ yAxisIndex: 0 }, level)) : chart.getHeight() / 2
      chart.dispatchAction({
        type: 'updateAxisPointer',
        x: Number(chart.convertToPixel({ xAxisIndex: 0 }, time)), y,
        axesInfo: [
          { axisDim: 'x', axisIndex: 0, value: time },
          ...(level == null ? [] : [{ axisDim: 'y', axisIndex: 0, value: level }]),
        ],
      }, { silent: true })
    }
    onTime(time == null ? null : Math.floor(time / 1000) as Time)
    for (const peer of peers) {
      if (peer === chart || peer.isDisposed()) continue
      const x = time == null ? NaN : Number(peer.convertToPixel({ xAxisIndex: 0 }, time))
      const y = peer.getHeight() / 2
      const visible = Number.isFinite(x) && peer.containPixel({ gridIndex: 0 }, [x, y])
      peer.dispatchAction(visible ? {
        type: 'updateAxisPointer', x, y,
        axesInfo: [{ axisDim: 'x', axisIndex: 0, value: time }],
      } : { type: 'updateAxisPointer', currTrigger: 'leave' }, { silent: true })
    }
  }
  chart.on('updateAxisPointer', pointer)
  return () => {
    if (!chart.isDisposed()) chart.off('updateAxisPointer', pointer)
    peers.delete(chart)
    if (peers.size === 0) groups.delete(group)
  }
}

export function useChartCrosshair(chart: ECharts | null, group: string, onTime: (time: Time | null) => void, interval: string) {
  useEffect(() => {
    if (chart && !chart.isDisposed()) return bindChartCrosshair(chart, group, onTime, interval)
  }, [chart, group, onTime, interval])
}
